import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Ticket,
  HeadphonesIcon,
  Film,
  Phone,
  Users,
  DollarSign,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  Bell,
  ShieldCheck,
  Shield,
  Webhook,
  ScanLine,
  Send,
  Gift,
  ClipboardList,
  ShieldAlert,
  Smartphone,
  Share2,
} from 'lucide-react';
import { cn } from '../utils/helpers';
import { getCurrentUser, isAdmin, roleLabels, AdminRole } from '../types/admin';
import { useServiceRequestPendingCount } from '../hooks/useServiceRequests';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Get current user from localStorage
  const currentUser = useMemo(() => getCurrentUser(), []);
  const showAdminLink = isAdmin();
  const { count: serviceRequestPendingCount } = useServiceRequestPendingCount();

  // Base navigation items
  const navigation = useMemo(() => {
    const items = [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Tickets', href: '/tickets', icon: Ticket },
      { name: 'Customer Support', href: '/support', icon: HeadphonesIcon },
      { name: 'Content Review', href: '/content-review', icon: Film },
      { name: 'Website App Demo', href: '/demo-content', icon: Smartphone },
      { name: 'Call Center', href: '/calls', icon: Phone },
      { name: 'Users', href: '/users', icon: Users },
      { name: 'Finance', href: '/finance', icon: DollarSign },
      { name: 'Webhooks', href: '/webhooks', icon: Webhook },
      { name: 'Background Checks', href: '/background-checks', icon: ScanLine },
      { name: 'Service Requests', href: '/service-requests', icon: ClipboardList },
      { name: 'Notifications', href: '/notifications', icon: Send },
      { name: 'Referrals', href: '/referrals', icon: Gift },
      { name: 'Affiliate Partners', href: '/affiliate-partners', icon: Share2 },
      { name: 'Safety Incidents', href: '/safety-incidents', icon: ShieldAlert },
      { name: 'Analytics', href: '/analytics', icon: BarChart3 },
      { name: 'Settings', href: '/settings', icon: Settings },
    ];
    
    // Add Admin Users link for admins/super admins
    if (showAdminLink) {
      items.push({ 
        name: 'Admin Users', 
        href: '/admin', 
        icon: currentUser?.role === 'SUPER_ADMIN' ? ShieldCheck : Shield 
      });
    }
    
    return items;
  }, [showAdminLink, currentUser?.role]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('adminUser');
    navigate('/login');
  };

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border',
          'transform transition-transform duration-300 ease-in-out',
          'lg:translate-x-0 lg:static lg:inset-0 flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-2">
            <img src="/xuman-logo.png" alt="Xuman" className="w-8 h-8" />
            <span className="text-lg font-bold text-foreground">
              Xuman <span className="text-primary">Admin</span>
            </span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-primary/10 transition-colors"
          >
            <X className="h-5 w-5 text-foreground" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto scrollbar-thin">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200',
                  active
                    ? 'bg-primary/15 text-primary border border-primary/30 shadow-glow-sm'
                    : 'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
                )}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium">{item.name}</span>
                {item.name === 'Customer Support' && (
                  <span className="ml-auto bg-primary/20 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
                    New
                  </span>
                )}
                {item.name === 'Service Requests' && serviceRequestPendingCount > 0 && (
                  <span className="ml-auto bg-warning/20 text-warning text-xs font-semibold px-2 py-0.5 rounded-full">
                    {serviceRequestPendingCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-border">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-primary/10 transition-colors"
            >
              <Menu className="h-5 w-5 text-foreground" />
            </button>
            <h1 className="text-lg font-semibold text-foreground hidden sm:block">
              {navigation.find((n) => isActive(n.href))?.name || 'Dashboard'}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <button className="relative p-2 rounded-lg hover:bg-primary/10 transition-colors">
              <Bell className="h-5 w-5 text-muted-foreground" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full" />
            </button>

            {/* User menu */}
            <div className="flex items-center gap-2 pl-3 border-l border-border">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                {currentUser?.role === 'SUPER_ADMIN' ? (
                  <ShieldCheck className="h-4 w-4 text-purple-400" />
                ) : currentUser?.role === 'ADMIN' ? (
                  <Shield className="h-4 w-4 text-blue-400" />
                ) : (
                  <User className="h-4 w-4 text-primary" />
                )}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-foreground">
                  {currentUser?.name || 'Admin'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {currentUser?.role ? roleLabels[currentUser.role as AdminRole] : 'Support Agent'}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-hidden">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
